import { Anchor, Badge, Box, Breadcrumbs, Group, Text, Title } from '@mantine/core';
import type { ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { navLinks } from '../../config/navLinks.js';
import { useIsMobile } from '../../hooks/useIsMobile.js';
import { RowLabelInline } from '../Diff/RecordLabel.js';
import { usePageHeaderState } from '../PageHeader/PageHeaderContext.js';
import { crumbLabel, headerCrumbs } from './headerCrumbs.js';

/** The page's header: trail and title left, actions right. On a phone: the burger and the title only. */
export const Header = ({ burger }: { burger?: ReactNode }) => {
    const { title, count, breadcrumbs, actions } = usePageHeaderState();
    const { pathname } = useLocation();
    const isMobile = useIsMobile();
    const crumbs = headerCrumbs(pathname, navLinks, title, breadcrumbs);

    return (
        <Group h="100%" px={{ base: 'sm', sm: 'lg' }} gap={isMobile ? 'xs' : 'md'} wrap="nowrap">
            {burger}
            <Box style={{ flex: 1, minWidth: 0 }}>
                {!isMobile && crumbs.length > 0 && (
                    <Breadcrumbs
                        separator="→"
                        separatorMargin="xs"
                        styles={{
                            root: { flexWrap: 'nowrap', whiteSpace: 'nowrap', overflow: 'hidden' },
                            separator: { color: 'var(--mantine-color-dimmed)' },
                        }}
                    >
                        {crumbs.map((crumb, i) =>
                            crumb.to ? (
                                <Anchor key={`${crumb.label}-${i}`} component={Link} to={crumb.to} fz="xs" c="dimmed">
                                    <RowLabelInline label={crumbLabel(crumb)} secondaryOpacity={0.7} />
                                </Anchor>
                            ) : (
                                <Text key={`${crumb.label}-${i}`} fz="xs" c="dimmed">
                                    <RowLabelInline label={crumbLabel(crumb)} secondaryOpacity={0.7} />
                                </Text>
                            ),
                        )}
                    </Breadcrumbs>
                )}
                {title && (
                    <Group gap="sm" wrap="nowrap" mt={isMobile ? 0 : 4} style={{ overflow: 'hidden' }}>
                        <Title
                            order={1}
                            fz={{ base: 'h2', sm: 'h1' }}
                            lh={1.2}
                            style={{
                                flexShrink: 0,
                                maxWidth: '100%',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                            }}
                        >
                            {title}
                        </Title>
                        {!isMobile && count !== undefined && (
                            <Badge
                                variant="default"
                                radius="xl"
                                tt="none"
                                fw={500}
                                style={{ flexShrink: 1, minWidth: '3rem' }}
                            >
                                {count}
                            </Badge>
                        )}
                    </Group>
                )}
            </Box>
            {!isMobile && actions && (
                <Group ml="auto" gap="xs" wrap="nowrap" style={{ flexShrink: 0 }}>
                    {actions}
                </Group>
            )}
        </Group>
    );
};
